import { useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { soaStore, soaActions } from '../../stores/soaStore';
import { authState } from '../../stores/authStore';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { formatDate, formatDateTime } from '../../utils/dateUtils';

interface DocumentVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: () => void;
}

export function DocumentVersionModal({ isOpen, onClose, onSave }: DocumentVersionModalProps) {
  const soaSnap = useSnapshot(soaStore);

  const [companyForm, setCompanyForm] = useState({
    issueNo: soaSnap.companyInfo.issueNo,
    date: soaSnap.companyInfo.date,
    preparedBy: soaSnap.companyInfo.preparedBy || '',
    reviewedBy: soaSnap.companyInfo.reviewedBy || '',
    approvedBy: soaSnap.companyInfo.approvedBy || '',
    approvedDate: soaSnap.companyInfo.approvedDate || '',
    nextReviewDate: soaSnap.companyInfo.nextReviewDate || '',
  });
  const [versionDescription, setVersionDescription] = useState('');

  // Sync form state when modal opens (ensures latest store values)
  useEffect(() => {
    if (isOpen) {
      setCompanyForm({
        issueNo: soaSnap.companyInfo.issueNo,
        date: soaSnap.companyInfo.date,
        preparedBy: soaSnap.companyInfo.preparedBy || '',
        reviewedBy: soaSnap.companyInfo.reviewedBy || '',
        approvedBy: soaSnap.companyInfo.approvedBy || '',
        approvedDate: soaSnap.companyInfo.approvedDate || '',
        nextReviewDate: soaSnap.companyInfo.nextReviewDate || '',
      });
      setVersionDescription('');
    }
  }, [isOpen]);

  const saveCompanyInfo = () => {
    const authSnap = authState.user;
    const changedBy = authSnap ? `${authSnap.firstName} ${authSnap.lastName}`.trim() : 'Unknown';
    const newEntry = {
      issueNo: companyForm.issueNo,
      date: companyForm.date,
      changedBy,
      description: versionDescription || 'Version updated',
      timestamp: new Date().toISOString(),
    };
    const existingHistory = soaSnap.companyInfo.versionHistory || [];
    soaActions.setCompanyInfo({
      ...companyForm,
      versionHistory: [newEntry, ...existingHistory],
    });
    onClose();
    onSave?.();
  };

  const incrementVersion = (type: 'minor' | 'major') => {
    const current = companyForm.issueNo;
    const parts = current.split('.');
    if (parts.length === 2) {
      const major = parseInt(parts[0], 10);
      const minor = parseInt(parts[1], 10);
      if (!isNaN(major) && !isNaN(minor)) {
        if (type === 'minor') {
          setCompanyForm({ ...companyForm, issueNo: `${major}.${String(minor + 1).padStart(2, '0')}` });
        } else {
          setCompanyForm({ ...companyForm, issueNo: `${major + 1}.00` });
        }
        return;
      }
    }
    // Fallback for non-standard format
    if (type === 'major') {
      setCompanyForm({ ...companyForm, issueNo: '1.00' });
    } else {
      setCompanyForm({ ...companyForm, issueNo: current + '.01' });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Document Version Control"
      subtitle="Version details, signatories, and review schedule"
      size="lg"
      gradient="from-gray-600 to-gray-700"
    >
      <div className="space-y-4 max-h-[75vh] overflow-y-auto">
        {/* Section 1: Version Details */}
        <div>
          <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-2">Version Details</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Issue Number</label>
              <input
                type="text"
                value={companyForm.issueNo}
                onChange={(e) => setCompanyForm({ ...companyForm, issueNo: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="1.00"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => incrementVersion('minor')}
                  className="flex-1 px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-300 whitespace-nowrap"
                  title="Increment minor version (e.g. 1.01 → 1.02)"
                >
                  Minor +
                </button>
                <button
                  type="button"
                  onClick={() => incrementVersion('major')}
                  className="flex-1 px-3 py-1.5 text-xs font-medium bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg border border-indigo-300 whitespace-nowrap"
                  title="Increment major version (e.g. 1.05 → 2.00)"
                >
                  Major +
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={companyForm.date}
                onChange={(e) => setCompanyForm({ ...companyForm, date: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
          <div className="mt-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Description of Changes</label>
            <input
              type="text"
              value={versionDescription}
              onChange={(e) => setVersionDescription(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              placeholder="Describe what changed in this version..."
            />
          </div>
        </div>

        {/* Section 2: Document Signatories */}
        <div>
          <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-2">Document Signatories</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Prepared By</label>
              <input
                type="text"
                value={companyForm.preparedBy}
                onChange={(e) => setCompanyForm({ ...companyForm, preparedBy: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Name of preparer"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Reviewed By</label>
              <input
                type="text"
                value={companyForm.reviewedBy}
                onChange={(e) => setCompanyForm({ ...companyForm, reviewedBy: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Name of reviewer"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Approved By</label>
              <input
                type="text"
                value={companyForm.approvedBy}
                onChange={(e) => setCompanyForm({ ...companyForm, approvedBy: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                placeholder="Name of approver"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Approved Date</label>
              <input
                type="date"
                value={companyForm.approvedDate}
                onChange={(e) => setCompanyForm({ ...companyForm, approvedDate: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        {/* Section 3: Review Schedule */}
        <div>
          <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-2">Review Schedule</h3>
          <div className="flex items-end gap-3">
            <div className="w-48">
              <label className="block text-xs font-medium text-gray-700 mb-1">Next Review Date</label>
              <input
                type="date"
                value={companyForm.nextReviewDate}
                onChange={(e) => setCompanyForm({ ...companyForm, nextReviewDate: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            {companyForm.nextReviewDate && (() => {
              const reviewDate = new Date(companyForm.nextReviewDate);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const diffDays = Math.ceil((reviewDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
              if (diffDays < 0) {
                return <span className="text-xs text-red-600 font-medium pb-1.5">Overdue by {Math.abs(diffDays)} day{Math.abs(diffDays) !== 1 ? 's' : ''}</span>;
              } else if (diffDays <= 30) {
                return <span className="text-xs text-amber-600 font-medium pb-1.5">Due in {diffDays} day{diffDays !== 1 ? 's' : ''}</span>;
              } else {
                return <span className="text-xs text-green-600 font-medium pb-1.5">Due in {diffDays} days</span>;
              }
            })()}
          </div>
        </div>

        {/* Section 4: Version History */}
        {(soaSnap.companyInfo.versionHistory || []).length > 0 && (
          <div>
            <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-2">
              Version History ({(soaSnap.companyInfo.versionHistory || []).length})
            </h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden max-h-36 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Issue</th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Changed By</th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Saved</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(soaSnap.companyInfo.versionHistory || []).map((entry, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-2 py-1.5 font-medium text-gray-900">{entry.issueNo}</td>
                      <td className="px-2 py-1.5 text-gray-600">{entry.date ? formatDate(entry.date) : '-'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{entry.changedBy}</td>
                      <td className="px-2 py-1.5 text-gray-600">{entry.description}</td>
                      <td className="px-2 py-1.5 text-gray-500">{formatDateTime(entry.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex gap-3 pt-3 border-t">
          <Button onClick={saveCompanyInfo} className="flex-1">
            Save Version
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
